import { useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Predefined colors - simple flat list
const PRESET_COLORS = [
  // Core colors
  { name: 'Red', value: '#EF4444', className: 'bg-red-500' },
  { name: 'Orange', value: '#F97316', className: 'bg-orange-500' },
  { name: 'Amber', value: '#D97706', className: 'bg-amber-600' },
  { name: 'Yellow', value: '#EAB308', className: 'bg-yellow-500' },
  { name: 'Green', value: '#22C55E', className: 'bg-green-500' },
  { name: 'Emerald', value: '#10B981', className: 'bg-emerald-500' },
  { name: 'Teal', value: '#14B8A6', className: 'bg-teal-500' },
  { name: 'Cyan', value: '#06B6D4', className: 'bg-cyan-500' },
  { name: 'Sky', value: '#0EA5E9', className: 'bg-sky-500' },
  { name: 'Blue', value: '#3B82F6', className: 'bg-blue-500' },
  { name: 'Indigo', value: '#6366F1', className: 'bg-indigo-500' },
  { name: 'Violet', value: '#A78BFA', className: 'bg-violet-500' },
  { name: 'Purple', value: '#A855F7', className: 'bg-purple-500' },
  { name: 'Fuchsia', value: '#D946EF', className: 'bg-fuchsia-500' },
  { name: 'Pink', value: '#EC4899', className: 'bg-pink-500' },
  { name: 'Rose', value: '#F43F5E', className: 'bg-rose-500' },
  // Neutrals
  { name: 'Black', value: '#000000', className: 'bg-black' },
  { name: 'Dark Gray', value: '#374151', className: 'bg-gray-700' },
  { name: 'Gray', value: '#6B7280', className: 'bg-gray-500' },
  { name: 'Light Gray', value: '#D1D5DB', className: 'bg-gray-300' },
  // No color option (last)
  { name: 'No Color', value: null, className: 'bg-base-200' },
];

const ColorPicker = ({ selectedColor, onColorSelect, onClose }) => {
  const [customColor, setCustomColor] = useState(selectedColor || '#3B82F6');
  const [activeTab, setActiveTab] = useState('preset'); // 'preset' or 'custom'

  const handleColorClick = (color) => {
    onColorSelect(color);
    onClose();
  };

  const handleCustomColorSubmit = () => {
    onColorSelect(customColor);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-[90%] max-w-[450px]">
        {/* Header */}
        <div className="p-4 border-b border-base-300 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Choose a Color</h3>
          <button
            onClick={onClose}
            className="btn btn-sm btn-ghost"
            title="Close"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-base-300">
          <button
            onClick={() => setActiveTab('preset')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              activeTab === 'preset'
                ? 'bg-base-200 border-b-2 border-primary text-primary'
                : 'text-base-content/70 hover:bg-base-200/50'
            }`}
          >
            Preset Colors
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              activeTab === 'custom'
                ? 'bg-base-200 border-b-2 border-primary text-primary'
                : 'text-base-content/70 hover:bg-base-200/50'
            }`}
          >
            Custom Color
          </button>
        </div>

        {/* Selected Color Preview */}
        <div className="p-4 bg-base-50 border-b border-base-300">
          <div className="flex items-center space-x-3">
            <span className="text-sm text-base-content/60">Current:</span>
            <div className="flex items-center space-x-2">
              {selectedColor === null ? (
                <>
                  <div className="w-8 h-8 rounded-lg border-2 border-base-300 shadow-sm bg-base-200 flex items-center justify-center">
                    <XMarkIcon className="w-4 h-4 text-red-500" />
                  </div>
                  <span className="text-sm font-medium">No Color</span>
                </>
              ) : (
                <>
                  <div
                    className="w-8 h-8 rounded-lg border-2 border-base-300 shadow-sm"
                    style={{ backgroundColor: selectedColor }}
                  />
                  <span className="text-sm font-medium">
                    {PRESET_COLORS.find(c => c.value === selectedColor)?.name || 'Custom Color'}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {activeTab === 'preset' ? (
            <div className="grid grid-cols-8 gap-2">
              {PRESET_COLORS.map((color) => (
                <div key={color.value || 'none'} className="tooltip" data-tip={color.name}>
                  <button
                    onClick={() => handleColorClick(color.value)}
                    className={`
                      relative w-10 h-10 rounded-lg transition-all duration-200
                      hover:scale-110 group shadow-sm border
                      ${color.value === null ? 'border-base-300 bg-base-200' : 'border-transparent'}
                      ${selectedColor === color.value
                        ? 'ring-2 ring-primary shadow-lg'
                        : 'hover:ring-2 hover:ring-primary/30'
                      }
                    `}
                    style={{ backgroundColor: color.value || undefined }}
                  >
                    {color.value === null ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <XMarkIcon className="w-5 h-5 text-red-500" />
                      </div>
                    ) : selectedColor === color.value ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-full p-0.5">
                          <CheckIcon className="w-3 h-3 text-gray-800" />
                        </div>
                      </div>
                    ) : null}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="label">
                  <span className="label-text">Hex Color Code</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    className="w-20 h-12 rounded cursor-pointer border border-base-300"
                  />
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    placeholder="#000000"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="input input-bordered flex-1 font-mono"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm text-base-content/60 mb-2">Preview</div>
                  <div
                    className="w-full h-20 rounded-lg border-2 border-base-300"
                    style={{ backgroundColor: customColor }}
                  />
                </div>
              </div>

              <button
                onClick={handleCustomColorSubmit}
                className="btn btn-primary w-full"
              >
                Use This Color
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-base-300 bg-base-50">
          <div className="text-sm text-base-content/60 text-center">
            {activeTab === 'preset'
              ? 'Click a color to select it'
              : 'Choose a custom color using the picker or enter a hex code'
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;